import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import './RecuperacaoCreditoV2.css';
import {
  FaHandHoldingUsd,
  FaExclamationTriangle,
  FaSearch,
  FaSyncAlt,
  FaCheckCircle,
  FaFileExport,
  FaTimes,
  FaFilter,
  FaWhatsapp,
  FaBarcode
} from 'react-icons/fa';
import { toast } from 'react-toastify';

function formatBRL(value) {
  const n = Number(value || 0);
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function formatDateBR(d) {
  if (!d) return '—';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return '—';
  return dt.toLocaleDateString('pt-BR', { timeZone: 'UTC' });
}

function daysDiffFromToday(dateStr) {
  if (!dateStr) return 0;
  const dt = new Date(dateStr);
  if (Number.isNaN(dt.getTime())) return 0;
  const now = new Date();
  const a = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const b = new Date(dt.getUTCFullYear(), dt.getUTCMonth(), dt.getUTCDate());
  const diff = Math.floor((a - b) / (1000 * 60 * 60 * 24));
  return diff; 
}

function downloadTextFile(filename, content, mime = 'text/plain;charset=utf-8') {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function buildCSV(rows) {
  const header = ['ID', 'Cliente', 'Descrição', 'Valor', 'Vencimento', 'Status', 'DiasAtraso'];
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const lines = [
    header.map(esc).join(';'),
    ...rows.map((r) => {
      const atraso = Math.max(0, daysDiffFromToday(r.data_vencimento));
      return [
        r.id,
        r.cliente_nome || r.cliente_nome_manual || '',
        r.descricao || '',
        Number(r.valor || 0),
        formatDateBR(r.data_vencimento),
        r.status || '',
        atraso,
      ].map(esc).join(';');
    }),
  ];
  return lines.join('\n');
}

export default function RecuperacaoCreditoV2({ user }) {
  const [loading, setLoading] = useState(true);
  const [kpis, setKpis] = useState({ total_pendente: 0, total_vencido: 0, vence_hoje: 0, recebido_mes: 0 });
  const [rows, setRows] = useState([]);
  const [fetchError, setFetchError] = useState('');

  // Contas Bancárias para Destino do Recebimento
  const [contasBancarias, setContasBancarias] = useState([]);
  const [contaDestino, setContaDestino] = useState('');

  const [status, setStatus] = useState('vencido');
  const [q, setQ] = useState('');
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');

  const [selectedIds, setSelectedIds] = useState(new Set());
  const [baixando, setBaixando] = useState(false);

  const [page, setPage] = useState(1);
  const pageSize = 10;

  const fetchKPIs = useCallback(async () => {
    try {
      const { data } = await api.get('/api/financeiro/kpis-receber');
      setKpis({
        total_pendente: Number(data?.total_pendente || 0),
        total_vencido: Number(data?.total_vencido || 0),
        vence_hoje: Number(data?.vence_hoje || 0),
        recebido_mes: Number(data?.recebido_mes || 0),
      });
    } catch {}
  }, []);

  const fetchContas = useCallback(async () => {
    setLoading(true);
    setFetchError('');
    try {
      const params = {};
      if (status && status !== 'todos') params.status = status;
      if (dataInicio) params.dataInicio = dataInicio;
      if (dataFim) params.dataFim = dataFim;

      const { data } = await api.get('/api/contas-a-receber', { params });
      setRows(Array.isArray(data) ? data : []);
      setSelectedIds(new Set());
      setPage(1);
    } catch (e) {
      setFetchError('Falha ao carregar Contas a Receber.');
      toast.error('Erro ao carregar Contas a Receber.');
    } finally {
      setLoading(false);
    }
  }, [status, dataInicio, dataFim]);

  const fetchContasBancarias = useCallback(async () => {
    try {
        const { data } = await api.get('/api/financeiro/conexoes');
        const ativas = data.filter(b => b.status === 'conectado' || b.conectado === true);
        setContasBancarias(ativas);
    } catch (err) {
        console.warn("Erro ao buscar contas bancárias", err);
    }
  }, []);

  useEffect(() => {
    fetchKPIs();
    fetchContas();
    fetchContasBancarias();
  }, [fetchKPIs, fetchContas, fetchContasBancarias]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return rows;
    return rows.filter((r) => {
      const hay = [r.id, r.descricao, r.cliente_nome, r.status].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(query);
    });
  }, [rows, q]);

  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, totalPages);

  const pageRows = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, safePage]);

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAllPage = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      pageRows.forEach((r) => {
        if ((r.status || '').toLowerCase() !== 'recebido') next.add(r.id);
      });
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  // Recebimento em Lote Enviando a Conta de Destino
  const baixarRecebidoLote = async () => {
    if (selectedIds.size === 0) {
      toast.warn('Selecione ao menos uma conta para dar baixa.');
      return;
    }
    setBaixando(true);
    try {
      await api.post('/api/contas-a-receber/receber-lote', { 
          ids: Array.from(selectedIds),
          conta_bancaria_id: contaDestino || null
      });
      toast.success('Baixa realizada com sucesso!');
      setSelectedIds(new Set());
      await fetchKPIs();
      await fetchContas();
    } catch {
      toast.error('Falha ao dar baixa nas contas.');
    } finally {
      setBaixando(false);
    }
  };

  const exportCSV = () => {
    const csv = buildCSV(filtered);
    downloadTextFile(`recuperacao_credito_${new Date().toISOString().slice(0, 10)}.csv`, csv, 'text/csv;charset=utf-8');
    toast.info('CSV exportado.');
  };

  const resetFilters = () => {
    setStatus('vencido');
    setQ('');
    setDataInicio('');
    setDataFim('');
  };

  const handleCobrarWhatsApp = (conta) => {
    const cliente = conta.cliente_nome || 'Cliente';
    const valorFormatado = formatBRL(conta.valor);
    const texto = `Olá ${cliente}! Consta em nosso sistema um título pendente referente a "${conta.descricao}" no valor de ${valorFormatado}. Podemos ajudar enviando a 2ª via do boleto?`;
    const url = `https://wa.me/?text=${encodeURIComponent(texto)}`;
    window.open(url, '_blank');
  };

  const handleGerarSegundaVia = (conta) => {
    toast.info(`Gerando 2ª via do boleto (OS/Ref: #${conta.id})...`);
    console.log("Simulando chamada API para 2ª via:", conta);
  };

  return (
    <div className="recovery2-page">
      <div className="recovery2-header">
        <div className="recovery2-title">
          <FaHandHoldingUsd className="recovery2-icon" />
          <div>
            <h1>Recuperação de Crédito</h1>
            <p>Central de cobrança e gestão de inadimplência.</p>
          </div>
        </div>

        <div className="recovery2-actions">
          <button className="recovery2-btn" onClick={() => { fetchKPIs(); fetchContas(); }}>
            <FaSyncAlt /> Atualizar
          </button>
          <button className="recovery2-btn secondary" onClick={exportCSV}>
            <FaFileExport /> Exportar
          </button>
          
          {/* SELETOR DE CONTA E BOTÃO DE BAIXA */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', borderLeft: '2px solid #e2e8f0', paddingLeft: '12px', marginLeft: '4px' }}>
              <span style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: 'bold' }}>Destino:</span>
              <select
                  style={{ border: '1px solid #cbd5e1', borderRadius: '8px', padding: '0.45rem', outline: 'none', fontWeight: '600', color: '#0f172a' }}
                  value={contaDestino}
                  onChange={(e) => setContaDestino(e.target.value)}
              >
                  <option value="">System Hub (Caixa Principal)</option>
                  {contasBancarias.map(b => (
                      <option key={b.id} value={b.id}>{b.nome}</option>
                  ))}
              </select>

              <button
                className="recovery2-btn primary"
                onClick={baixarRecebidoLote}
                disabled={baixando || selectedIds.size === 0}
                title="Dar baixa (recebido) nas contas selecionadas"
              >
                <FaCheckCircle /> {baixando ? 'Baixando…' : `Dar Baixa (${selectedIds.size})`}
              </button>
          </div>
        </div>
      </div>

      <div className="recovery2-tip">
        <FaExclamationTriangle />
        <div>
          <b>Dica de Cobrança:</b> Utilize os botões na coluna de ações para enviar mensagens de WhatsApp pré-formatadas ou emitir a 2ª via de boletos atrasados. Selecione o banco de destino ao dar baixa.
        </div>
      </div>

      <div className="recovery2-kpis">
        <div className="recovery2-kpi">
          <span>Pendente</span><strong>{formatBRL(kpis.total_pendente)}</strong><small>Em aberto</small>
        </div>
        <div className="recovery2-kpi warn">
          <span>Vencido</span><strong>{formatBRL(kpis.total_vencido)}</strong><small>Atrasado</small>
        </div>
        <div className="recovery2-kpi info">
          <span>Vence Hoje</span><strong>{formatBRL(kpis.vence_hoje)}</strong><small>Prioridade do dia</small>
        </div>
        <div className="recovery2-kpi success">
          <span>Recebido (mês)</span><strong>{formatBRL(kpis.recebido_mes)}</strong><small>Performance</small>
        </div>
      </div>

      <div className="recovery2-filters">
        <div className="recovery2-search">
          <FaSearch />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por cliente, descrição, ID…" />
        </div>
        <div className="recovery2-filter">
          <FaFilter />
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="todos">Todos</option>
            <option value="pendente">Pendente</option>
            <option value="vencido">Vencido</option>
            <option value="recebido">Recebido</option>
          </select>
        </div>
        <div className="recovery2-date">
          <label>De</label><input type="date" value={dataInicio} onChange={(e) => setDataInicio(e.target.value)} />
        </div>
        <div className="recovery2-date">
          <label>Até</label><input type="date" value={dataFim} onChange={(e) => setDataFim(e.target.value)} />
        </div>
        <button className="recovery2-btn" onClick={fetchContas}><FaSyncAlt /> Aplicar</button>
        <button className="recovery2-btn ghost" onClick={resetFilters}><FaTimes /> Limpar</button>
      </div>

      <div className="recovery2-tableWrap">
        {fetchError && (
          <div className="recovery2-error">
            <FaExclamationTriangle />
            <div>
              <b>Erro:</b> {fetchError}
              <div className="recovery2-errorActions">
                <button className="recovery2-btn primary" onClick={fetchContas}><FaSyncAlt /> Tentar novamente</button>
              </div>
            </div>
          </div>
        )}

        <table className="recovery2-table">
          <thead>
            <tr>
              <th style={{ width: 44 }}>
                <input
                  type="checkbox"
                  onChange={(e) => (e.target.checked ? selectAllPage() : clearSelection())}
                  checked={pageRows.length > 0 && pageRows.filter((r) => (r.status || '').toLowerCase() !== 'recebido').every((r) => selectedIds.has(r.id))}
                />
              </th>
              <th>Origem</th>
              <th>Cliente</th>
              <th>Descrição</th>
              <th>Vencimento</th>
              <th>Atraso</th>
              <th>Valor</th>
              <th>Ações</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              [...Array(6)].map((_, i) => (
                <tr key={i}><td colSpan={8} className="recovery2-skeleton">Carregando…</td></tr>
              ))
            ) : pageRows.length === 0 ? (
              <tr><td colSpan={8} className="recovery2-empty">Nenhum registro encontrado.</td></tr>
            ) : (
              pageRows.map((r) => {
                const atraso = Math.max(0, daysDiffFromToday(r.data_vencimento));
                const st = (r.status || '').toLowerCase();
                const canSelect = st !== 'recebido';
                const isBoleto = r.origem_tipo === 'venda_manual' || r.origem_tipo === 'manual';

                return (
                  <tr key={r.id} className={atraso >= 15 ? 'risk' : ''}>
                    <td>
                      <input type="checkbox" disabled={!canSelect} checked={selectedIds.has(r.id)} onChange={() => toggleSelect(r.id)} />
                    </td>
                    <td>
                      {isBoleto ? (
                         <span style={{ color: '#059669', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '4px' }}>
                           <FaBarcode /> Boleto
                         </span>
                      ) : (
                         <span style={{ color: '#2563eb', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '4px' }}>
                           <FaHandHoldingUsd /> OS/Auto
                         </span>
                      )}
                    </td>
                    <td className="mono">{r.cliente_nome || '—'}</td>
                    <td title={r.descricao || ''}>{r.descricao || '—'}</td>
                    <td>{formatDateBR(r.data_vencimento)}</td>
                    <td>
                      {atraso > 0 ? (
                        <span className={`badge ${atraso >= 15 ? 'badge-danger' : 'badge-warn'}`}>{atraso}d</span>
                      ) : (
                        <span className="badge badge-ok">OK</span>
                      )}
                    </td>
                    <td className="value">{formatBRL(r.valor)}</td>
                    <td>
                       <div style={{ display: 'flex', gap: '8px' }}>
                          <button 
                             onClick={() => handleCobrarWhatsApp(r)}
                             style={{ background: 'none', border: 'none', color: '#25D366', cursor: 'pointer', fontSize: '1.2rem' }}
                             title="Cobrar via WhatsApp"
                          >
                             <FaWhatsapp />
                          </button>
                          {isBoleto && st !== 'recebido' && (
                             <button 
                               onClick={() => handleGerarSegundaVia(r)}
                               style={{ background: 'none', border: 'none', color: '#475569', cursor: 'pointer', fontSize: '1.2rem' }}
                               title="Gerar 2ª Via do Boleto"
                             >
                               <FaBarcode />
                             </button>
                          )}
                       </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>

        {!loading && total > 0 && (
          <div className="recovery2-footer">
            <div className="recovery2-footerLeft">
              Mostrando <b>{pageRows.length}</b> de <b>{total}</b> | Página <b>{safePage}</b> / <b>{totalPages}</b>
            </div>
            <div className="recovery2-footerRight">
              <button className="recovery2-btn" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={safePage === 1}>◀</button>
              <button className="recovery2-btn" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={safePage === totalPages}>▶</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}